import { landmineNames } from '@mm/constants/landmine-names';
import ChatbotCRUDActions from '@mm/state/actions/ChatbotCRUDActions';
import ConnectedPageActions from '@mm/state/actions/ConnectedPageCRUDActions';
import AdCampaignController from '@mm/state/controllers/AdCampaignController';
import { AdCampaignModel } from '@mm/state/model/AdCampaignModel';
import { MessengerAdModel } from '@mm/state/model/MessengerAdModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { getErrorJson, LifeCycleThis } from '@mm/utils';
import AutoSyncIcon from 'assets/icons/ads-auto-sync.svg';
import ManualSyncIcon from 'assets/icons/ads-manual-sync.svg';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose, lifecycle, withStateHandlers } from 'recompose';
import { Dispatch } from 'redux';
import CollapsibleContentContainer from '../../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import './AdDetailsSync.scss';
import AutomaticSync from './AutomaticSync';
import ManualSync from './ManualSync';

type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;

type PropsFromStateHandlers = {
  autoSync: boolean;
  adCampaigns: AdCampaignModel[];
  setAutoSync: (value: boolean) => void;
  setAdCampaigns: (value: AdCampaignModel[]) => void;
  campaignsError: string;
  setCampaignsError: (value: string) => void;
  setAdAccountName: (value: string) => void;
};

type InnerProps = PropsFromDispatch & PropsFromState & PropsFromStateHandlers;

type OuterProps = {
  ad: MessengerAdModel;
  adAccountName: string;
  adAccountId: string;
  accountUserId: number;
  botId: number;
  usedInSlideout?: boolean;
};

type Props = InnerProps & OuterProps;

const AdDetailsSync = (props: Props) => {
  const {
    ad,
    autoSync,
    setAutoSync,
    adCampaigns,
    facebookPageId,
    botId,
    campaignsError,
    adAccountName,
    adAccountId,
    accountUserId,
    markAdsPublished,
    usedInSlideout,
  } = props;

  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const blockFeature = !featureFlags.adsUnlimitedNewCtmAds;
  const canDisableAutoSync = !featureFlags.adsNewCtmAdsAutomaticSyncDisable;
  const modalActions = useModalActions();

  const getOnClickOverride = useCallback(() => {
    if (blockFeature) {
      return () =>
        modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['MESSAGING-ADS__AD-DETAILS-SYNC'],
          feature: 'adsUnlimitedNewCtmAds',
        });
    }
  }, [blockFeature]);

  const classList = classNames('ad-details-sync__monkey-image-wrapper', {
    'ad-details-sync__inner-shadow': autoSync,
  });

  return (
    <CollapsibleContentContainer
      orderNumber="4"
      containerName="Sync your opt-in dialogue to Ads in a campaign"
      expandedOnMount={!blockFeature}
      proRibbonType="unicorn"
      onClickOverride={getOnClickOverride()}
    >
      <div className="ad-details__section-container ad-details-sync">
        <div className="ad-details-sync__header">
          <div
            className={classNames('ad-details-sync__monkey-image-wrapper', {
              'ad-details-sync__inner-shadow': !autoSync,
            })}
            onClick={() => setAutoSync(true)}
          >
            <div className="ad-details-sync__image-title">Automatically</div>
            <div className="ad-details-sync__monkey-image">
              <img src={AutoSyncIcon} alt="" />
            </div>
          </div>
          <div
            className={classList}
            onClick={() => {
              if (canDisableAutoSync || !autoSync) {
                setAutoSync(false);
              } else {
                modalActions.openUniversalUpgradeLandmineModal({
                  landmineName:
                    landmineNames['MESSAGING-ADS__CLICK_MANUAL_SYNC'],
                  feature: 'adsNewCtmAdsAutomaticSyncDisable',
                });
              }
            }}
          >
            <div className="ad-details-sync__image-title">Manually</div>
            <div className="ad-details-sync__monkey-image">
              <img src={ManualSyncIcon} alt="" />
            </div>
          </div>
        </div>
        <div className="ad-details-sync__body">
          {autoSync ? (
            <AutomaticSync
              adCampaigns={adCampaigns}
              facebookPageId={facebookPageId}
              markAdsPublished={markAdsPublished}
              ad={ad}
              botId={botId}
              campaignsError={campaignsError}
              adAccountName={adAccountName}
              adAccountId={adAccountId}
              accountUserId={accountUserId}
            />
          ) : (
            <ManualSync ad={ad} />
          )}
        </div>
      </div>
    </CollapsibleContentContainer>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  markAdsPublished: () =>
    dispatch({ type: ConnectedPageActions.adPublished, key: 'hasCtmAds' }),
  retrieveChatbot: (botId: number) =>
    dispatch(ChatbotCRUDActions.retrieve(botId)),
});

const mapStateToProps = (state: StoreState) => ({
  activeChatbot: state.chatbot.activeChatbot,
  facebookPageId: activeFacebookPageIdSelector(state),
});

async function componentDidMount(this: LifeCycleThis<Props>) {
  const { setAdCampaigns, setCampaignsError, facebookPageId, adAccountId } =
    this.props;

  if (adAccountId === 'none') {
    return;
  }

  try {
    const { adCampaigns } =
      await AdCampaignController.retrieveMessagesCampaigns(
        facebookPageId,
        adAccountId,
        true,
      );
    setAdCampaigns(adCampaigns);
  } catch (e) {
    const json = await getErrorJson(e);
    const errorMessage = json.errors || '';
    setCampaignsError(errorMessage);
  }
}

async function componentDidUpdate(
  this: LifeCycleThis<Props>,
  prevProps: Props,
) {
  const { setAdCampaigns, setCampaignsError, facebookPageId, adAccountId } =
    this.props;
  const prevAccountId = prevProps.adAccountId;

  if (prevAccountId === adAccountId || adAccountId === 'none') {
    return;
  }

  try {
    const { adCampaigns } =
      await AdCampaignController.retrieveMessagesCampaigns(
        facebookPageId,
        adAccountId,
        true,
      );
    setAdCampaigns(adCampaigns);
    setCampaignsError('');
  } catch (e) {
    const json = await getErrorJson(e);
    const errorMessage = json.errors || '';
    setAdCampaigns([]);
    setCampaignsError(errorMessage);
  }
}

const initialState = {
  autoSync: true,
  adCampaigns: [],
  campaignsError: '',
};

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, {
    setAutoSync: () => value => ({ autoSync: value }),
    setAdCampaigns: () => adCampaigns => ({ adCampaigns }),
    setCampaignsError: () => campaignsError => ({ campaignsError }),
  }),
  lifecycle<Props, {}>({ componentDidMount, componentDidUpdate }),
);

export default enhance(AdDetailsSync);
